import * as React from 'react';
import { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { SyncIcon, ListIcon, MapMarkedAltIcon } from '@patternfly/react-icons';
import toast from 'react-hot-toast';
import { AdminScreen, ScreenStatusLevel, STATUS_ORDER } from '@app/Screens/types';
import { getScreens } from '@app/Screens/screens.api';
import { ScreensTable } from '@app/Screens/ScreensTable';
import { ScreensMap } from '@app/Screens/ScreensMap';
import { STATUS_META } from '@app/Screens/StatusBadge';
import { relativeTime } from '@app/Screens/format';
import { colors, radii, shadows } from '@app/components/designTokens';

const REFRESH_MS = 15000;
type StatusFilter = 'ALL' | ScreenStatusLevel;
type ViewMode = 'list' | 'map';

export const Screens: React.FunctionComponent = () => {
  const navigate = useNavigate();
  const [screens, setScreens] = useState<AdminScreen[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [search, setSearch] = useState('');
  const [view, setView] = useState<ViewMode>('list');

  const load = useCallback(async (showSpinner: boolean) => {
    if (showSpinner) setLoading(true);
    try {
      const data = await getScreens();
      setScreens(data);
      setError(null);
      setLastUpdated(new Date().toISOString());
    } catch (e: any) {
      setError(e?.message || 'Failed to load screens');
      if (!showSpinner) toast.error('Could not refresh screens');
    } finally {
      if (showSpinner) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(true);
    const t = setInterval(() => load(false), REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);

  const counts = useMemo(() => {
    const c = { ALL: screens.length, ONLINE: 0, STALE: 0, OFFLINE: 0 };
    screens.forEach(s => {
      c[s.status] = (c[s.status] || 0) + 1;
    });
    return c;
  }, [screens]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return screens
      .filter(s => statusFilter === 'ALL' || s.status === statusFilter)
      .filter(s => {
        if (!q) return true;
        return (
          (s.displayName || '').toLowerCase().includes(q) ||
          (s.clientId || '').toLowerCase().includes(q) ||
          (s.pairedDriverName || '').toLowerCase().includes(q) ||
          (s.currentAdName || '').toLowerCase().includes(q) ||
          String(s.screenId).includes(q)
        );
      })
      .sort((a, b) => {
        // Surface problems first: OFFLINE, then STALE, then ONLINE.
        if (STATUS_ORDER[a.status] !== STATUS_ORDER[b.status]) {
          return STATUS_ORDER[a.status] - STATUS_ORDER[b.status];
        }
        return (a.displayName || '').localeCompare(b.displayName || '');
      });
  }, [screens, statusFilter, search]);

  const onSelect = (screenId: number) => navigate(`/screens/${screenId}`);

  const styles: { [k: string]: React.CSSProperties } = {
    wrap: { padding: 24 },
    headerRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: 12,
      marginBottom: 16
    },
    title: { fontSize: 22, fontWeight: 700, color: colors.title, margin: 0 },
    subtle: { fontSize: 13, color: colors.subtle },
    refreshBtn: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 8,
      padding: '8px 14px',
      border: `1px solid ${colors.border}`,
      borderRadius: radii.button,
      background: '#fff',
      color: colors.body,
      fontSize: 13,
      fontWeight: 600,
      cursor: 'pointer'
    },
    kpiRow: { display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 },
    toolbar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 12,
      flexWrap: 'wrap',
      marginBottom: 12
    },
    search: {
      flex: 1,
      minWidth: 220,
      maxWidth: 360,
      padding: '10px 12px',
      border: `1px solid ${colors.border}`,
      borderRadius: radii.input,
      fontSize: 14,
      outline: 'none'
    },
    toggleGroup: { display: 'inline-flex', border: `1px solid ${colors.border}`, borderRadius: radii.button, overflow: 'hidden' },
    card: { background: '#fff', borderRadius: radii.card, boxShadow: shadows.softCard, border: `1px solid ${colors.border}`, overflow: 'hidden' }
  };

  const kpiCard = (key: StatusFilter, label: string, value: number, dot?: string) => {
    const active = statusFilter === key;
    return (
      <button
        key={key}
        onClick={() => setStatusFilter(key)}
        style={{
          flex: '1 1 150px',
          textAlign: 'left',
          background: '#fff',
          border: `1px solid ${active ? colors.accent : colors.border}`,
          boxShadow: active ? `0 0 0 3px ${colors.accentRing}` : 'none',
          borderRadius: radii.card,
          padding: '14px 18px',
          cursor: 'pointer'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {dot && <span style={{ width: 9, height: 9, borderRadius: '50%', background: dot }} />}
          <span style={{ fontSize: 13, fontWeight: 600, color: colors.subtle }}>{label}</span>
        </div>
        <div style={{ fontSize: 26, fontWeight: 800, color: colors.title, marginTop: 4 }}>{value}</div>
      </button>
    );
  };

  const toggleBtn = (mode: ViewMode, label: string, icon: React.ReactNode) => {
    const active = view === mode;
    return (
      <button
        onClick={() => setView(mode)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 7,
          padding: '9px 14px',
          border: 'none',
          background: active ? colors.accent : '#fff',
          color: active ? '#fff' : colors.muted,
          fontSize: 13,
          fontWeight: 600,
          cursor: 'pointer'
        }}
      >
        {icon}
        {label}
      </button>
    );
  };

  return (
    <div style={styles.wrap}>
      <div style={styles.headerRow}>
        <div>
          <h1 style={styles.title}>Screens</h1>
          <div style={styles.subtle}>
            {lastUpdated ? `Updated ${relativeTime(lastUpdated)} · auto-refreshes every 15s` : 'Loading…'}
          </div>
        </div>
        <button style={styles.refreshBtn} onClick={() => load(true)} disabled={loading}>
          <SyncIcon className={loading ? 'logo-bolt-pulse' : undefined} />
          Refresh
        </button>
      </div>

      <div style={styles.kpiRow}>
        {kpiCard('ALL', 'All screens', counts.ALL)}
        {kpiCard('ONLINE', 'Online', counts.ONLINE, STATUS_META.ONLINE.dot)}
        {kpiCard('STALE', 'Stale', counts.STALE, STATUS_META.STALE.dot)}
        {kpiCard('OFFLINE', 'Offline', counts.OFFLINE, STATUS_META.OFFLINE.dot)}
      </div>

      <div style={styles.toolbar}>
        <input
          style={styles.search}
          placeholder="Search by name, client id, driver, ad…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <div style={styles.toggleGroup}>
          {toggleBtn('list', 'List', <ListIcon />)}
          {toggleBtn('map', 'Map', <MapMarkedAltIcon />)}
        </div>
      </div>

      {error && (
        <div
          style={{
            background: colors.dangerRing,
            color: colors.danger,
            borderRadius: 10,
            padding: '12px 16px',
            fontSize: 14,
            marginBottom: 12
          }}
        >
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ ...styles.card, padding: 48, textAlign: 'center', color: colors.subtle }}>
          Loading screens…
        </div>
      ) : view === 'list' ? (
        <div style={styles.card}>
          <ScreensTable screens={filtered} onSelect={onSelect} />
        </div>
      ) : (
        <ScreensMap screens={filtered} onSelect={onSelect} />
      )}
    </div>
  );
};
